import { computed, ref, watch } from 'vue';

const DEFAULT_ITEMS_PER_PAGE = 15;

/**
 * Provides pagination helpers for campaign lists without requiring backend changes.
 * @param {import('vue').Ref<Array>} campaignsRef - Reactive list of campaigns to paginate.
 * @param {number} itemsPerPage - Number of campaigns per page.
 */
export const useCampaignPagination = (
  campaignsRef,
  itemsPerPage = DEFAULT_ITEMS_PER_PAGE
) => {
  const currentPage = ref(1);

  const totalItems = computed(() => campaignsRef.value?.length || 0);
  const totalPages = computed(() =>
    Math.max(1, Math.ceil(totalItems.value / itemsPerPage))
  );

  const paginatedCampaigns = computed(() => {
    const list = campaignsRef.value || [];
    const startIndex = (currentPage.value - 1) * itemsPerPage;
    return list.slice(startIndex, startIndex + itemsPerPage);
  });

  const shouldShowPagination = computed(() => totalItems.value > itemsPerPage);

  const updateCurrentPage = page => {
    const parsed = Number(page) || 1;
    currentPage.value = Math.min(Math.max(parsed, 1), totalPages.value || 1);
  };

  watch(
    campaignsRef,
    () => {
      updateCurrentPage(1);
    },
    { immediate: true }
  );

  watch(totalPages, newTotal => {
    if (currentPage.value > newTotal) {
      currentPage.value = newTotal;
    }
  });

  return {
    currentPage,
    paginatedCampaigns,
    totalItems,
    shouldShowPagination,
    itemsPerPage,
    updateCurrentPage,
  };
};

export default useCampaignPagination;
